import {LessonModel} from '@/models/content/lesson';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_IMAGES_PATH } from '@/utils/constants';
import { gutCourseNames, truncateString } from '@/utils/utils';
import moment from 'moment';
import { taxonomySerializer } from './taxonomy';

export class LessonSerializer {
  private static INSTANCE = new LessonSerializer();

  static get instance() {
    return this.INSTANCE;
  }

  public serializeLesson(lesson: any): LessonModel {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = lesson.thumbnail ?
      contentCdnUrl + lesson.thumbnail :
        `${window.location.origin}/` + DEFAULT_IMAGES_PATH.unit;
    const result: LessonModel = {
      id: lesson.id,
      title: lesson.title,
      assessmentCount: lesson.assessmentCount,
      collectionCount: lesson.collectionCount,
      description: lesson.learningObjective,
      format: lesson.format || 'lesson',
      efficacy: lesson.efficacy,
      relevance: lesson.relevance,
      engagement: lesson.engagement,
      standards: taxonomySerializer.normalizeTaxonomyObject(lesson.taxonomy),
      creator: taxonomySerializer.normalizeCreator(lesson.creator),
      thumbnailUrl,
    };
    return result;
  }

  /**
   * Normalize the Search lesson response
   *
   * @param payload is the endpoint response in JSON format
   * @returns {Lesson[]}
   */
   public normalizeSearchLessons(payload: any) {
    if (Array.isArray(payload.searchResults)) {
      return payload.searchResults.map((result: any) => {
        return this.normalizeLesson(result);
      });
    }
  }

  /**
   * Normalizes a lesson
   * @param {*} result
   * @returns {Course}
   */
  public normalizeLesson(result: any) {
    const serializer = this;
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = result.thumbnail ?
      contentCdnUrl + result.thumbnail :
        `${window.location.origin}/` + DEFAULT_IMAGES_PATH.unit;
    return {
      id: result.id,
      title: result.title,
      description: result.description,
      createdDate: result.addDate,
      thumbnailUrl,
      lastModified: result.lastModified,
      lastModifiedBy: result.lastModifiedBy,
      isVisibleOnProfile: result.visibleOnProfile,
      isPublished: result.publishStatus === 'published',
      assessmentCount: result.assessmentCount,
      collectionCount: result.collectionCount,
      standards: null,
      owner: result.owner ? taxonomySerializer.normalizeOwner(result.owner) : null,
      sequence: result.sequence,
      relevance: result.relevance,
      efficacy: result.efficacy,
      engagement: result.engagement,
      type: result.format,
    };
  }

  public lessonPulloutSerializer(course: any, coursePullOutData: any) {
    const defaultVectorValue = 0.5;
    let resultSet = {};
    if (course) {
      resultSet = {
        descriptive: {
          title: course.title,
          description: truncateString(course.description),
        },

        creation: {
          'Created On':
            moment(course.createdDate).format('MMMM DD, YYYY') || null,
          'Publisher': 'Gooru Org',
          'Publish Status': course.isPublished ? 'Published' : 'Unpublished',
          'Aggregator': course.aggregator ? course.aggregator : null,
          'Modified On':
            moment(course.lastModified).format('MMMM DD, YYYY') || null,
          'Modified By':
            course.lastModifiedUser.username || course.lastModifiedBy,
          'License': coursePullOutData.license
            ? coursePullOutData.license
            : 'Public Domain',
          'Host': null,
        },

        educational: {
          Audience: course.audience,
        },

        media: {
          Keywords: null,
          Visibility: coursePullOutData.visible_on_profile ? 'True' : 'False',
        },

        instructional: {
          '21st Century Skills': null,
        },

        framework: {
          subject: course.taxonomySubject,
          Course: gutCourseNames(course.taxonomyCourse),
          domain: course.taxonomyDomain,
          Standards: course.standards || [],
        },

        vector: {
          relevance: course.relevance || defaultVectorValue,
          engagement: course.engagement || defaultVectorValue,
          efficacy: course.efficacy || defaultVectorValue,
        },
      };
    }
    return resultSet;
  }

}

export const lessonSerializer = LessonSerializer.instance;
